import { useApiRequest } from '@/shared/api/useApiRequest'
import type { SupplierUser, SupplierUserRole } from '@/types/supplier'

export interface CreateSupplierUserData {
  userId: string
  role: SupplierUserRole
  position?: string
  workPhone?: string
  department?: string
  notes?: string
}

export interface UpdateSupplierUserData {
  role?: SupplierUserRole
  position?: string
  workPhone?: string
  department?: string
  isActive?: boolean
  canManageProducts?: boolean
  canManageOrders?: boolean
  canManageUsers?: boolean
  canViewReports?: boolean
  canManageProfile?: boolean
  notes?: string
}

export interface SupplierUserSearchFilters {
  supplierId?: string
  role?: SupplierUserRole[]
  isActive?: boolean
  department?: string
  search?: string
}

export interface SupplierUserSearchResponse {
  users: SupplierUser[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

/**
 * API client для работы с пользователями поставщиков
 */
export function useSupplierUserAPI() {
  const { makeRequest } = useApiRequest()

  /**
   * Получение пользователей поставщика
   */
  const getSupplierUsers = async (
    supplierId: string,
    filters: SupplierUserSearchFilters = {},
    pagination: { page?: number; limit?: number } = { page: 1, limit: 20 },
  ): Promise<SupplierUserSearchResponse> => {
    const params = Object.fromEntries(
      Object.entries({
        ...filters,
        ...pagination,
      }).filter(([_, v]) => v !== undefined),
    ) as Record<string, string | number | boolean | string[]>

    return await makeRequest<SupplierUserSearchResponse>(
      `/api/suppliers/${supplierId}/users`,
      {
        method: 'GET',
      },
      params,
    )
  }

  /**
   * Получение пользователя поставщика по ID
   */
  const getSupplierUser = async (id: string): Promise<SupplierUser> => {
    return await makeRequest<SupplierUser>(`/api/supplier-users/${id}`, {
      method: 'GET',
    })
  }

  /**
   * Создание нового пользователя поставщика
   */
  const createSupplierUser = async (
    supplierId: string,
    data: CreateSupplierUserData,
  ): Promise<SupplierUser> => {
    return await makeRequest<SupplierUser>(`/api/suppliers/${supplierId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Обновление пользователя поставщика
   */
  const updateSupplierUser = async (
    id: string,
    data: UpdateSupplierUserData,
  ): Promise<SupplierUser> => {
    return await makeRequest<SupplierUser>(`/api/supplier-users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Удаление пользователя поставщика
   */
  const deleteSupplierUser = async (id: string): Promise<void> => {
    await makeRequest(`/api/supplier-users/${id}`, {
      method: 'DELETE',
    })
  }

  /**
   * Приглашение пользователя
   */
  const inviteSupplierUser = async (
    supplierId: string,
    data: {
      email: string
      role: SupplierUserRole
      position?: string
      department?: string
      notes?: string
    },
  ): Promise<{
    message: string
    invitation: {
      id: string
      email: string
      role: SupplierUserRole
      status: 'pending' | 'sent' | 'accepted' | 'expired'
      invitedAt?: string
    }
  }> => {
    return await makeRequest(`/api/suppliers/${supplierId}/users/invite`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Активация пользователя
   */
  const activateSupplierUser = async (id: string): Promise<SupplierUser> => {
    return await makeRequest<SupplierUser>(`/api/supplier-users/${id}/activate`, {
      method: 'POST',
    })
  }

  /**
   * Деактивация пользователя
   */
  const deactivateSupplierUser = async (id: string, reason?: string): Promise<SupplierUser> => {
    return await makeRequest<SupplierUser>(`/api/supplier-users/${id}/deactivate`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  }

  /**
   * Получение активных пользователей поставщика
   */
  const getActiveSupplierUsers = async (supplierId: string): Promise<SupplierUser[]> => {
    return await makeRequest<SupplierUser[]>(`/api/suppliers/${supplierId}/users/active`, {
      method: 'GET',
    })
  }

  /**
   * Получение пользователей по роли
   */
  const getSupplierUsersByRole = async (
    supplierId: string,
    role: SupplierUserRole,
  ): Promise<SupplierUser[]> => {
    return await makeRequest<SupplierUser[]>(`/api/suppliers/${supplierId}/users/role/${role}`, {
      method: 'GET',
    })
  }

  /**
   * Получение данных для форм
   */
  const getFormData = async (): Promise<{
    roles: Array<{ value: SupplierUserRole; label: string }>
    departments: string[]
    positions: string[]
  }> => {
    return await makeRequest('/api/supplier-users/form-data', {
      method: 'GET',
    })
  }

  return {
    getSupplierUsers,
    getSupplierUser,
    createSupplierUser,
    updateSupplierUser,
    deleteSupplierUser,
    inviteSupplierUser,
    activateSupplierUser,
    deactivateSupplierUser,
    getActiveSupplierUsers,
    getSupplierUsersByRole,
    getFormData,
  }
}

export const supplierUserApi = () => useSupplierUserAPI()
